import React, { FC } from 'react';
import { AttachItemsGlassProps } from '@components/common/AttachItemsGlass';
import { StyleProp, Text, TextStyle, View, ViewStyle } from 'react-native';
import { scale as initialScale } from '@components/frame';
import { font as initialFont } from '@styles/fonts';
import { ODataOrRetrievedDocumentLink } from '@v2/components/imageHandling/types/types';
import { SCREEN_BORDER_RADIUS } from '@v2/pages/common/constants/base.constants';
import { GlassButton } from '@components/common/glassButton';
import Svg from '@styles/icons/svg';
import { SCOPE_ID_TO_TAG_NAME } from '@components/imageHandling/common';
import i18n from 'i18n-js';
import { IconConfig } from '@components/operatorPanel/interfaces';
import { useManageAttachItemGlass } from '@components/common/AttachItemsGlass/hooks/useManageAttachItemGlass';

interface AttachItemGlassProps extends Pick<AttachItemsGlassProps, 'C' | 'scale' | 'font'> {
  link: Pick<ODataOrRetrievedDocumentLink, 'scopeId' | 'scopeReferenceId' | 'scopeReferenceName'>;
  buttonStyle?: StyleProp<ViewStyle>;
  onAttachButtonPress?: () => void;
  onFavoriteIconPress?: () => void;
  textStyle?: StyleProp<TextStyle>;
  iconConfig?: Partial<IconConfig>;
  contextKey?: string | null;
}

const AttachItemGlass: FC<AttachItemGlassProps> = ({
  C,
  link,
  buttonStyle,
  onAttachButtonPress,
  onFavoriteIconPress,
  textStyle,
  iconConfig,
  contextKey,
  scale = initialScale,
  font = initialFont,
}) => {
  const { handleGlassButtonPress, favoriteRef, isFavorite } = useManageAttachItemGlass({
    scopeId: link.scopeId,
    scopeReferenceId: link.scopeReferenceId,
    onAttachButtonPress,
    onFavoriteIconPress,
    contextKey,
  });

  const FavoriteIcon = (
    <Svg
      icon={isFavorite ? 'starSelected' : 'starUnselected'}
      width={scale(14)}
      height={scale(14)}
      color={isFavorite ? C('Label/Light/Primary') : C('Label/Light/Quinary')}
    />
  );

  return (
    <GlassButton
      style={[
        {
          borderRadius: scale(SCREEN_BORDER_RADIUS),
          paddingHorizontal: scale(10),
          paddingVertical: scale(3),
          justifyContent: 'center',
          alignItems: 'center',
        },
        buttonStyle,
      ]}
      scale={scale}
      onPress={handleGlassButtonPress}
    >
      {() => (
        <View style={{ flexDirection: 'row', gap: scale(5), alignItems: 'center' }}>
          <Svg icon="clipCheck" width={scale(28)} height={scale(28)} color={C('Label/Light/Primary')} {...iconConfig} />
          <Text numberOfLines={1} style={[font['Subhead 2'], { color: C('Label/Light/Primary') }, textStyle]}>{`${i18n.t(
            SCOPE_ID_TO_TAG_NAME[link.scopeId] ?? ''
          )} ${link.scopeReferenceName}`}</Text>
          {onFavoriteIconPress ? (
            <View ref={favoriteRef}>
              {FavoriteIcon}
            </View>
          ) : (
            FavoriteIcon
          )}
        </View>
      )}
    </GlassButton>
  );
};

export default AttachItemGlass;
